import { browserTimezone, convertToTimezone, convertToUtc } from "@gigpro/utils/datetime";
import { makeApi } from "@zodios/core";
import { makeClientWithHooks } from "api/helpers";
import { gigStatusSchema, gigTypeSchema } from "api/resources/shared";
import { compareAsc } from "date-fns";
import { z } from "zod";

export const eventSchema = z.object({
  id: z.string(),
  name: z
    .string()
    .min(3, "Event name must be at least 3 characters")
    .max(25, "Event name must be 25 characters or less"),
  organization_id: z.string(),
});
export type Event = z.infer<typeof eventSchema>;

export const eventShiftSchema = z.object({
  id: z.number(),
  start_time: z.coerce.date(),
  end_time: z.coerce.date(),
  location_id: z.coerce.number(),
  location: z.object({
    id: z.number(),
    name: z.string(),
    street_address: z.string(),
    city: z.string(),
    state_code: z.string(),
    zip_code: z.string(),
    timezone: z.string().catch(browserTimezone),
  }),
  contact_name: z.string(),
  contact_phone: z.string(),
});
export type EventShift = z.infer<typeof eventShiftSchema>;

export const eventShiftWithGigsSchema = eventShiftSchema.extend({
  gigs: z.array(
    z.object({
      id: z.number(),
      skill_name: z.string(),
      status: gigStatusSchema,
      type: gigTypeSchema,
      wage_amount: z.coerce.number(),
      allowed_confirmed_pros: z.number(),
      current_confirmed_pros_count: z.number(),
      address: z.object({
        id: z.number(),
        street_address: z.string(),
        city: z.string(),
        state_code: z.string(),
        postal_code: z.string(),
        timezone: z.string().catch(browserTimezone),
      }),
    }),
  ),
});
export type EventShiftWithGigs = z.infer<typeof eventShiftWithGigsSchema>;

const eventShiftUpdatePayloadSchema = z.union([
  z.object({ start_time: z.coerce.date(), end_time: z.coerce.date(), timezone: z.string() }),
  z.object({ contact_name: z.string(), contact_phone: z.string() }),
]);
const eventShiftUpdateResponseSchema = z.union([
  z.object({ start_time: z.coerce.date(), end_time: z.coerce.date() }),
  z.object({ contact_name: z.string(), contact_phone: z.string() }),
]);

const eventApi = makeApi([
  {
    method: "post",
    path: "/events/",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: eventSchema.omit({ id: true }),
      },
    ],
    response: eventSchema,
    alias: "createEvent",
  },
  {
    method: "delete",
    path: "/events/:id/",
    response: z.undefined(),
    alias: "deleteEvent",
  },
  {
    method: "get",
    path: "/events/:id/",
    response: eventSchema,
    alias: "event",
  },

  {
    method: "patch",
    path: "/events/:id/",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: eventSchema.omit({ id: true }),
      },
    ],
    response: eventSchema,
    alias: "updateEvent",
  },
  {
    method: "get",
    path: "/events/:id/shifts/",
    response: z.array(eventShiftSchema),
    alias: "eventShifts",
  },
  {
    method: "get",
    path: "/events/:id/shifts/?is_including_gigs=true",
    response: z.array(eventShiftWithGigsSchema).transform((shifts) =>
      shifts
        .map((shift) => ({
          ...shift,
          start_time: convertToTimezone(shift.start_time, shift.location.timezone),
          end_time: convertToTimezone(shift.end_time, shift.location.timezone),
          gigs: shift.gigs.filter((gig) => gig.status !== "CANCELLED"),
        }))
        .filter(({ gigs }) => gigs.length > 0)
        .toSorted((a, b) => compareAsc(a.start_time, b.start_time)),
    ),
    alias: "eventShiftsWithGigs",
  },
  {
    method: "get",
    path: "/staffer/organizations/:orgId/shifts/",
    parameters: [
      {
        type: "Query",
        name: "start_time",
        schema: z.coerce.date(),
      },
      {
        type: "Query",
        name: "location_id",
        schema: z.number().optional(),
      },
      {
        type: "Query",
        name: "types",
        schema: z
          .array(gigTypeSchema)
          .transform((types) => types.map(String).join(","))
          .optional(),
      },
      {
        type: "Query",
        name: "excluded_event_ids",
        schema: z
          .array(z.string())
          .transform((ids) => ids.join(","))
          .optional(),
      },
    ],
    response: z
      .array(eventShiftWithGigsSchema)
      .transform((shifts) =>
        shifts.filter(({ gigs }) => gigs.length > 0).toSorted((a, b) => compareAsc(a.start_time, b.start_time)),
      ),
    alias: "organizationShifts",
  },
  {
    method: "post",
    path: "/events/:eventId/shifts/",
    parameters: [
      {
        name: "Shift IDs",
        type: "Body",
        schema: z.object({ gig_shift_ids: z.array(z.number()) }),
      },
    ],
    response: z.array(eventShiftSchema),
    alias: "addShiftsToEvent",
  },
  {
    method: "patch",
    path: "/staffer/gigs/gig-shifts/:shiftId/",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: eventShiftUpdatePayloadSchema.transform((updateData) => {
          if ("contact_name" in updateData) return updateData;
          return {
            start_time: convertToUtc(updateData.start_time, updateData.timezone),
            end_time: convertToUtc(updateData.end_time, updateData.timezone),
          };
        }),
      },
    ],
    response: eventShiftUpdateResponseSchema,
    alias: "updateEventShift",
  },
]);

const { hooks } = makeClientWithHooks("event", eventApi);
export { hooks as eventHooks };
